import { useCallback, useState } from "react";
import { cn } from "@/lib/utils";
import type { Ticket } from "@/modules/types";

/** Resolved shows this many days by default; older ones sit behind "Show older". */
export const RESOLVED_RECENT_DAYS = 30;

export const isRecentlyResolved = (
  ticket: Pick<Ticket, "resolved_at" | "updated_at">,
  now = Date.now(),
) => {
  const at = ticket.resolved_at ?? ticket.updated_at;
  if (!at) return true;
  return now - Date.parse(at) <= RESOLVED_RECENT_DAYS * 24 * 60 * 60 * 1000;
};

const readIdList = (storageKey: string): string[] => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) ?? "[]");
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

/** Column ids remembered in this browser (collapsed columns). */
export const usePersistedIdList = (storageKey: string) => {
  const [state, setState] = useState(() => ({
    storageKey,
    ids: readIdList(storageKey),
  }));
  // Another board (e.g. another pipeline) keeps its own list.
  const ids =
    state.storageKey === storageKey ? state.ids : readIdList(storageKey);
  const toggle = useCallback(
    (id: string) => {
      const current =
        state.storageKey === storageKey ? state.ids : readIdList(storageKey);
      const next = current.includes(id)
        ? current.filter((entry) => entry !== id)
        : [...current, id];
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Private mode: the choice lasts until reload.
      }
      setState({ storageKey, ids: next });
    },
    [state, storageKey],
  );
  return [ids, toggle] as const;
};

/**
 * Where a card dropped at `visibleIndex` of a filtered column lands in the
 * full column (the column may hide older tickets).
 */
export const insertIndexForVisibleDrop = <T>(
  full: T[],
  visible: T[],
  visibleIndex: number,
) => {
  const anchor = visible[visibleIndex];
  if (anchor !== undefined) return full.indexOf(anchor);
  const last = visible[visible.length - 1];
  return last === undefined ? 0 : full.indexOf(last) + 1;
};

/** Card shell shared by board cards; tilts and lifts while dragged. */
export const boardCardClassName = (isDragging: boolean) =>
  cn(
    "relative w-full overflow-hidden rounded-lg border bg-card text-left transition-[box-shadow,transform]",
    isDragging
      ? "rotate-2 shadow-overlay ring-1 ring-border"
      : "hover:shadow-raised",
  );
